import React, { useMemo } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { Alert, Box, CircularProgress, Paper, Stack, Typography, useTheme } from "@mui/material";

const panelSx = {
  width: "100%",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
  p: { xs: 2, md: 2.5 },
  height: "100%",
};

const EdadSexoChart = ({ data, loading, error }) => {
  const theme = useTheme();
  const sourceRows = data?.totals;
  const { chartData, hasData } = useMemo(() => {
    const rows = Array.isArray(sourceRows) ? sourceRows : [];

    return {
      chartData: rows.map((row) => ({
        grupo: row.rango,
        Mujer: Number(row.mujeres),
        Hombre: Number(row.hombres),
        "Sin dato": Number(row.sexoSinDato),
      })),
      hasData: rows.some(
        (row) =>
          Number(row.atenciones) > 0 ||
          Number(row.mujeres) > 0 ||
          Number(row.hombres) > 0 ||
          Number(row.sexoSinDato) > 0
      ),
    };
  }, [sourceRows]);

  return (
    <Paper sx={panelSx} data-testid="edad-sexo-panel">
      <Typography variant="h5" sx={{ fontWeight: 850 }}>
        Distribución por edad y sexo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Atenciones registradas por grupo etario.
      </Typography>

      {error ? (
        <Alert severity="error">No fue posible cargar esta información.</Alert>
      ) : loading ? (
        <Stack minHeight={320} alignItems="center" justifyContent="center" spacing={1.5}>
          <CircularProgress aria-label="Cargando distribución por edad y sexo" />
          <Typography color="text.secondary">Cargando distribución...</Typography>
        </Stack>
      ) : !hasData ? (
        <Box minHeight={320} display="flex" alignItems="center" justifyContent="center" textAlign="center">
          <Typography color="text.secondary">No hay datos disponibles para este período.</Typography>
        </Box>
      ) : (
        <Box height={350} minWidth={0}>
          <ResponsiveBar
            data={chartData}
            keys={["Mujer", "Hombre", "Sin dato"]}
            indexBy="grupo"
            groupMode="grouped"
            margin={{ top: 12, right: 24, bottom: 72, left: 52 }}
            padding={0.28}
            colors={["#008080", "#009fe3", "#7b8794"]}
            borderRadius={3}
            enableLabel={false}
            axisTop={null}
            axisRight={null}
            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 5 }}
            axisBottom={{ tickSize: 0, tickPadding: 8 }}
            legends={[
              {
                dataFrom: "keys",
                anchor: "bottom",
                direction: "row",
                translateY: 58,
                itemWidth: 100,
                itemHeight: 18,
                symbolSize: 11,
              },
            ]}
            theme={{
              axis: { ticks: { text: { fill: theme.palette.text.secondary } } },
              legends: { text: { fill: theme.palette.text.secondary } },
              tooltip: { container: { color: theme.palette.text.primary } },
            }}
            tooltip={({ id, value, indexValue }) => (
              <Paper sx={{ p: 1.25, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">{indexValue}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {id}: {Number(value).toLocaleString("es-VE")}
                </Typography>
              </Paper>
            )}
            role="application"
            ariaLabel="Distribución por edad y sexo"
          />
        </Box>
      )}
    </Paper>
  );
};

export default EdadSexoChart;
